import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import Header from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Users, Mail, Phone,
  Star, Target, Sparkles, ArrowRight, BarChart3,
  Award, Loader2, Search, MessageSquare, CheckCircle,
  Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend,
  LineChart, Line
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NichePerformance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Time trends state
  const [timeTrends, setTimeTrends] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState('weekly');
  const [loadingTrends, setLoadingTrends] = useState(true);

  const fetchPerformanceData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/niche-performance`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch performance data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTimeTrends = useCallback(async () => {
    setLoadingTrends(true);
    try {
      const response = await axios.get(`${API_URL}/dashboard/time-trends?period=${trendPeriod}`);
      setTimeTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch time trends:', error);
    } finally {
      setLoadingTrends(false);
    }
  }, [trendPeriod]);

  useEffect(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  useEffect(() => {
    fetchTimeTrends();
  }, [fetchTimeTrends]);

  const getPerformanceColor = (score) => {
    if (score >= 70) return 'text-emerald-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-slate-400';
  };

  const getPerformanceBadge = (score) => {
    if (score >= 70) return { label: 'High Performer', class: 'badge-success' };
    if (score >= 40) return { label: 'Growing', class: 'badge-warning' };
    return { label: 'Needs Data', class: 'badge-neutral' };
  };

  const CHART_COLORS = [
    '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const hasData = data?.niches?.length > 0;

  return (
    <DashboardLayout>
      <Header 
        title="Niche Performance" 
        subtitle="Analyze which niches deliver the best response rates"
      />
      
      <div className="p-8" data-testid="niche-performance-content">
        {hasData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="card-dashboard">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Active Niches</p>
                    <p className="text-3xl font-heading font-bold text-slate-900">
                      {data.summary.total_niches_active}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </div>

              <div className="card-dashboard">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Total Leads</p>
                    <p className="text-3xl font-heading font-bold text-slate-900">
                      {data.summary.total_leads}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="card-dashboard">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Overall Response Rate</p>
                    <p className="text-3xl font-heading font-bold text-emerald-600">
                      {data.summary.overall_response_rate}%
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </div>

              <div className="card-dashboard bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600 mb-1">Top Performer</p>
                    <p className="text-lg font-heading font-bold text-indigo-700 truncate">
                      {data.summary.top_performing_niche || 'N/A'}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Award className="w-6 h-6 text-indigo-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Response Rate by Niche */}
              <div className="card-dashboard">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Response Rate by Niche
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.niches} layout="vertical" margin={{ left: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" domain={[0, 100]} unit="%" stroke="#94a3b8" fontSize={12} />
                      <YAxis 
                        type="category" 
                        dataKey="niche" 
                        stroke="#94a3b8" 
                        fontSize={11}
                        width={120}
                        tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value}%`, 'Response Rate']}
                      />
                      <Bar dataKey="response_rate" radius={[0, 4, 4, 0]}>
                        {data.niches.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Leads Distribution */}
              <div className="card-dashboard">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-indigo-600" />
                  Leads Distribution
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.niches}
                        dataKey="total_leads"
                        nameKey="niche"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ niche, percent }) => 
                          percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''
                        }
                        labelLine={false}
                      >
                        {data.niches.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                        formatter={(value, name) => [value, 'Leads']}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value) => value.length > 20 ? value.substring(0, 20) + '...' : value}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Time-Based Trends Section */}
            <div className="card-dashboard mb-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                  Performance Trends
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant={trendPeriod === 'weekly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTrendPeriod('weekly')}
                    className={trendPeriod === 'weekly' ? 'btn-primary' : ''}
                  >
                    Weekly
                  </Button>
                  <Button
                    variant={trendPeriod === 'monthly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTrendPeriod('monthly')}
                    className={trendPeriod === 'monthly' ? 'btn-primary' : ''}
                  >
                    Monthly
                  </Button>
                </div>
              </div>

              {loadingTrends ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                </div>
              ) : timeTrends?.trends?.length > 0 ? (
                <>
                  {/* Period Comparison Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Leads Added</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {timeTrends.current_period?.leads_added || 0}
                      </p>
                      {timeTrends.changes && (
                        <div className={`flex items-center gap-1 text-xs mt-1 ${
                          timeTrends.changes.leads_added >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {timeTrends.changes.leads_added >= 0 ? (
                            <ArrowUpRight className="w-3 h-3" />
                          ) : (
                            <ArrowDownRight className="w-3 h-3" />
                          )}
                          {Math.abs(timeTrends.changes.leads_added)} vs last {trendPeriod === 'weekly' ? 'week' : 'month'}
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Contacted</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {timeTrends.current_period?.contacted || 0}
                      </p>
                      {timeTrends.changes && (
                        <div className={`flex items-center gap-1 text-xs mt-1 ${
                          timeTrends.changes.contacted >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {timeTrends.changes.contacted >= 0 ? (
                            <ArrowUpRight className="w-3 h-3" />
                          ) : (
                            <ArrowDownRight className="w-3 h-3" />
                          )}
                          {Math.abs(timeTrends.changes.contacted)} vs last {trendPeriod === 'weekly' ? 'week' : 'month'}
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Replied</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {timeTrends.current_period?.replied || 0}
                      </p>
                      {timeTrends.changes && (
                        <div className={`flex items-center gap-1 text-xs mt-1 ${
                          timeTrends.changes.replied >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {timeTrends.changes.replied >= 0 ? (
                            <ArrowUpRight className="w-3 h-3" />
                          ) : (
                            <ArrowDownRight className="w-3 h-3" />
                          )}
                          {Math.abs(timeTrends.changes.replied)} vs last {trendPeriod === 'weekly' ? 'week' : 'month'}
                        </div>
                      )}
                    </div>

                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500 mb-1">Response Rate</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {timeTrends.current_period?.response_rate || 0}%
                      </p>
                      {timeTrends.changes && (
                        <div className={`flex items-center gap-1 text-xs mt-1 ${
                          timeTrends.changes.response_rate >= 0 ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {timeTrends.changes.response_rate >= 0 ? (
                            <ArrowUpRight className="w-3 h-3" />
                          ) : (
                            <ArrowDownRight className="w-3 h-3" />
                          )}
                          {Math.abs(timeTrends.changes.response_rate)}% vs last {trendPeriod === 'weekly' ? 'week' : 'month'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Trend Line Chart */}
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={timeTrends.trends} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="period" stroke="#94a3b8" fontSize={12} />
                        <YAxis stroke="#94a3b8" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="leads_added" 
                          name="Leads Added"
                          stroke="#6366f1" 
                          strokeWidth={2}
                          dot={{ fill: '#6366f1', strokeWidth: 2 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="contacted" 
                          name="Contacted"
                          stroke="#10b981" 
                          strokeWidth={2}
                          dot={{ fill: '#10b981', strokeWidth: 2 }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="replied" 
                          name="Replied"
                          stroke="#f59e0b" 
                          strokeWidth={2}
                          dot={{ fill: '#f59e0b', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No trend data available yet</p>
                  <p className="text-sm text-slate-400 mt-1">Start adding and contacting leads to see trends</p>
                </div>
              )}
            </div>

            {/* Niche Performance Table */}
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  Detailed Niche Performance
                </h3>
                <p className="text-sm text-slate-500">
                  Sorted by performance score
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Niche</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Leads</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Contacted</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Replied</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Response Rate</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Score</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.niches.map((niche, index) => {
                      const badge = getPerformanceBadge(niche.performance_score);
                      return (
                        <tr key={niche.niche} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td className="py-4 px-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' :
                              index === 1 ? 'bg-slate-100 text-slate-600' :
                              index === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-50 text-slate-500'
                            }`}>
                              {index + 1}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-indigo-500" />
                              <span className="font-medium text-slate-900">{niche.niche}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="font-semibold text-slate-700">{niche.total_leads}</span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Mail className="w-3 h-3 text-blue-500" />
                              <span className="text-slate-600">{niche.contacted}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <MessageSquare className="w-3 h-3 text-emerald-500" />
                              <span className="text-slate-600">{niche.replied}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className={`font-bold ${getPerformanceColor(niche.response_rate)}`}>
                              {niche.response_rate}%
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {niche.performance_score >= 50 ? (
                                <TrendingUp className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-slate-400" />
                              )}
                              <span className={`font-bold ${getPerformanceColor(niche.performance_score)}`}>
                                {niche.performance_score}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className={badge.class}>{badge.label}</span>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Link to={`/search?niche=${niche.niche_key}`}>
                              <Button variant="ghost" size="sm" data-testid={`find-more-${niche.niche_key}`}>
                                Find More <ArrowRight className="w-3 h-3 ml-1" />
                              </Button>
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Insights Section */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card-dashboard bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                <h4 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Recommendation
                </h4>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  {data.summary.top_performing_niche ? (
                    <>
                      <strong>{data.summary.top_performing_niche}</strong> is your best performing niche. 
                      Consider focusing more outreach efforts here to maximize response rates and conversions.
                    </>
                  ) : (
                    'Start reaching out to leads in different niches to identify your best performers.'
                  )}
                </p>
              </div>

              <div className="card-dashboard bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
                <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Pro Tip
                </h4>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Niches with higher response rates typically have specific pain points that your services directly address. 
                  Use the AI analysis to understand why certain niches respond better.
                </p>
              </div>
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="card-dashboard text-center py-16">
            <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
              No Performance Data Yet
            </h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Start saving leads and tracking your outreach to see which niches deliver the best response rates.
            </p>
            <Link to="/search">
              <Button className="btn-primary" data-testid="start-finding-leads">
                <Search className="w-4 h-4 mr-2" />
                Start Finding Leads
              </Button>
            </Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NichePerformance;
